import { css, styled } from "goober";
import { useEffect, useId, useRef, useState, type RefObject } from "react";
import type { ResearchSource } from "../../../shared/researchSources";
import { useNow } from "../../hooks/useNow";
import {
  researchSourceDate,
  researchSourcePreview,
} from "./researchSourcePreview";
import { faviconPath } from "../../lib/apiPaths";
import { zIndexes } from "../../lib/zIndexes";
import { AnchoredPopover } from "../AnchoredPopover";
import { Icon } from "../Icon";
import { CountBadge } from "../CountBadge";
import { Link } from "../Link";
import {
  ChatpadBody,
  ChatpadListHeader,
  ChatpadListScrollArea,
} from "../PersonalChats/ChatpadList";
import { StepDetails } from "../StepDetails";
import { EmptyState } from "../EmptyState";
import { Text } from "../Text";
import { TOOLTIP_DELAY } from "../Tooltip";
import { PressButton } from "../PressButton";
import { CollapsibleHeader } from "./Expand";
import type { ResearchSourceGroup } from "./ResearchCitations";

const pillClassName = css`
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 190px;
  height: 16px;
  padding: 0 6px 0 2px;
  margin: 0 2px;
  border-radius: 8px;
  background: var(--alpha8);
  color: var(--text-secondary);
  font-size: 9px;
  font-weight: 450;
  line-height: 12px;
  letter-spacing: -1%;
  vertical-align: text-bottom;
  white-space: nowrap;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  &:hover,
  &[aria-current="true"],
  &[aria-expanded="true"] {
    background: var(--alpha16);
    color: var(--text-primary);
  }
`;

const Label = styled("span")`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const Popover = styled(AnchoredPopover)`
  width: min(360px, calc(100vw / var(--ui-zoom, 1) - 24px));
  max-height: calc(100dvh / var(--ui-zoom, 1) - 24px);
  color: var(--text-primary);
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-high);
  z-index: ${zIndexes.tooltip};
  overflow: auto;
  text-align: left;
  font-family: var(--font-sans);
  font-size: 13px;
  line-height: 1.5;
`;

const SourcePills = styled("div")`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 16px 16px 8px;
  pointer-events: none;
  & > * {
    min-width: 0;
    margin: 0;
    pointer-events: auto;
  }
`;

const SourceCardLink = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-radius: 12px;
  color: var(--text-primary);
  text-decoration: none;
  overflow-wrap: anywhere;
  &[data-stretched]::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 12px;
  }
  &:hover:not([data-stretched]),
  &:focus-visible:not([data-stretched]) {
    background: var(--alpha4);
  }
  &:focus-visible {
    outline: none;
    @media (forced-colors: active) {
      outline: 2px solid Highlight;
    }
  }
`;

const SourcePreview = styled("div")`
  position: relative;
  &:hover,
  &:focus-within {
    background: var(--alpha4);
  }
`;

const LogoStack = styled("span")`
  display: inline-flex;
  padding-left: 4px;
  & > span {
    margin-left: -4px;
    border: 2px solid var(--surface-primary);
    border-radius: 50%;
    background: var(--surface-primary);
  }
`;

const SourceList = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
`;

export type CitationReference = {
  id: string;
  source?: ResearchSource;
  loading: boolean;
};

function sourceDomain(source: ResearchSource) {
  return new URL(source.url).hostname.replace(/^www\./, "");
}

function SourceLogo({ domain, size = 16 }: { domain: string; size?: 12 | 16 }) {
  const [failed, setFailed] = useState(false);
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        flexShrink: 0,
      }}
    >
      {failed ? (
        <Icon name="earth" size={12} />
      ) : (
        <img
          src={faviconPath(domain)}
          alt=""
          width={size === 12 ? 12 : 14}
          height={size === 12 ? 12 : 14}
          style={{ borderRadius: "50%", objectFit: "contain" }}
          onError={() => setFailed(true)}
        />
      )}
    </span>
  );
}

function SourceCard({
  source,
  titleId,
  onOpen,
  showSourcePill = true,
  stretchLink,
}: {
  source: ResearchSource;
  titleId?: string;
  onOpen?: () => void;
  showSourcePill?: boolean;
  stretchLink?: boolean;
}) {
  const domain = sourceDomain(source);
  const now = useNow(60_000);
  const date = researchSourceDate(source.publishDate, now);
  const preview = researchSourcePreview(source.preview);
  return (
    <SourceCardLink
      to={source.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={source.title}
      onClick={onOpen}
      data-stretched={stretchLink || undefined}
      style={showSourcePill ? undefined : { paddingTop: 0 }}
    >
      {showSourcePill && (
        <span
          className={pillClassName}
          style={{ alignSelf: "flex-start", margin: 0 }}
        >
          <SourceLogo key={domain} domain={domain} size={12} />
          <Label>{domain}</Label>
        </span>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span id={titleId}>
          <Text variant="text-regular-md" style={{ display: "block" }}>
            {source.title}
          </Text>
        </span>
        {(date || preview) && (
          <Text
            variant="text-regular"
            color="var(--text-secondary)"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {date}
            {date && preview ? " — " : ""}
            {preview}
          </Text>
        )}
      </div>
    </SourceCardLink>
  );
}

export function CitationPill({
  references,
}: {
  references: CitationReference[];
}) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [index, setIndex] = useState(0);
  const [placement, setPlacement] = useState({ above: false, maxHeight: 340 });
  const triggerRef = useRef<HTMLAnchorElement | HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const id = useId();
  const first = references.find((reference) => reference.source)?.source;
  const activeIndex = references[index] ? index : 0;
  const domain = first ? sourceDomain(first) : null;
  const label =
    domain ??
    (references.some((reference) => reference.loading)
      ? "Loading source…"
      : "Source unavailable");
  const cancelHover = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
  };
  const close = () => {
    cancelHover();
    setOpen(false);
    setPinned(false);
  };
  const show = () => {
    cancelHover();
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      const zoom = Number.parseFloat(
        getComputedStyle(document.documentElement).zoom
      );
      const below = window.innerHeight - rect.bottom;
      const above = below < 340 * zoom && rect.top > below;
      setPlacement({
        above,
        maxHeight: Math.max(0, (above ? rect.top : below) - 20) / zoom,
      });
    }
    setOpen(true);
  };
  const leave = () => {
    cancelHover();
    if (!pinned) hoverTimerRef.current = setTimeout(() => setOpen(false), 150);
  };
  useEffect(
    () => () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    },
    []
  );
  useEffect(() => {
    if (pinned && open)
      popoverRef.current
        ?.querySelector<HTMLElement>("button:not(:disabled), a")
        ?.focus();
  }, [pinned, open]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: Event) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      )
        return;
      setOpen(false);
      setPinned(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      setPinned(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("focusin", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("focusin", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);
  return (
    <span
      onPointerEnter={(event) => {
        if (event.pointerType === "touch") return;
        cancelHover();
        if (!open) hoverTimerRef.current = setTimeout(show, TOOLTIP_DELAY);
      }}
      onPointerLeave={leave}
    >
      <Popover
        inline
        id={id}
        role="dialog"
        aria-labelledby={`${id}-title`}
        isOpen={open}
        popoverRef={popoverRef}
        style={{
          maxHeight: placement.maxHeight,
          ...(placement.above
            ? { bottom: "anchor(top)", marginBottom: 8 }
            : { top: "anchor(bottom)", marginTop: 8 }),
          left: "clamp(12px, anchor(left), max(12px, calc(100vw / var(--ui-zoom, 1) - 372px)))",
        }}
        trigger={({ style }) => {
          const props = {
            ref: (node: HTMLAnchorElement | HTMLButtonElement | null) => {
              triggerRef.current = node;
            },
            style,
            className: pillClassName,
            "aria-label": first
              ? `Sources: ${first.title}${references.length > 1 ? ` and ${references.length - 1} more` : ""}`
              : label,
            "aria-expanded": open,
            "aria-controls": id,
            "aria-haspopup": "dialog" as const,
          };
          const content = (
            <>
              {domain ? (
                <SourceLogo key={domain} domain={domain} size={12} />
              ) : (
                <Icon name="earth" size={12} />
              )}
              <Label>{label}</Label>
              {references.length > 1 && <span>+{references.length - 1}</span>}
            </>
          );
          return first ? (
            <Link
              {...props}
              to={first.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={close}
              onKeyDown={(event) => {
                if (event.key !== "ArrowDown") return;
                event.preventDefault();
                show();
                setPinned(true);
              }}
            >
              {content}
            </Link>
          ) : (
            <PressButton
              {...props}
              onPress={() => {
                if (pinned) close();
                else {
                  show();
                  setPinned(true);
                }
              }}
            >
              {content}
            </PressButton>
          );
        }}
      >
        <SourcePreview onPointerEnter={cancelHover} onPointerLeave={leave}>
          {references.length > 1 && (
            <SourcePills role="group" aria-label="Sources">
              {references.map((reference, sourceIndex) => {
                const source = reference.source;
                const domain = source ? sourceDomain(source) : null;
                const label =
                  domain ??
                  (reference.loading
                    ? "Loading source…"
                    : "Source unavailable");
                const props = {
                  className: pillClassName,
                  "aria-current":
                    sourceIndex === activeIndex ? ("true" as const) : undefined,
                  onPointerEnter: () => setIndex(sourceIndex),
                  onFocus: () => setIndex(sourceIndex),
                };
                const content = (
                  <>
                    {domain ? (
                      <SourceLogo key={domain} domain={domain} size={12} />
                    ) : (
                      <Icon name="earth" size={12} style={{ flexShrink: 0 }} />
                    )}
                    <Label>{label}</Label>
                  </>
                );
                return source ? (
                  <Link
                    key={reference.id}
                    {...props}
                    aria-label={`Source: ${source.title}`}
                    to={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={close}
                  >
                    {content}
                  </Link>
                ) : (
                  <PressButton
                    key={reference.id}
                    {...props}
                    onPress={() => setIndex(sourceIndex)}
                  >
                    {content}
                  </PressButton>
                );
              })}
            </SourcePills>
          )}
          <div style={{ display: "grid" }}>
            {references.map((reference, sourceIndex) => (
              <div
                key={reference.id}
                aria-hidden={sourceIndex !== activeIndex || undefined}
                style={{
                  gridArea: "1 / 1",
                  visibility:
                    sourceIndex === activeIndex ? "visible" : "hidden",
                }}
              >
                {reference.source ? (
                  <SourceCard
                    source={reference.source}
                    titleId={
                      sourceIndex === activeIndex ? `${id}-title` : undefined
                    }
                    onOpen={close}
                    showSourcePill={references.length === 1}
                    stretchLink
                  />
                ) : (
                  <div style={{ padding: 16 }}>
                    <p
                      id={
                        sourceIndex === activeIndex ? `${id}-title` : undefined
                      }
                    >
                      {reference.loading
                        ? "Loading retrieved evidence…"
                        : "This reference has no available retrieved source."}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </SourcePreview>
      </Popover>
    </span>
  );
}

export function AnswerSources({
  sources,
  onShowSources,
}: {
  sources: ResearchSource[];
  onShowSources: () => void;
}) {
  return (
    <CollapsibleHeader
      onPointerDownCapture={(event) => event.preventDefault()}
      aria-label={`View ${sources.length} ${sources.length === 1 ? "source" : "sources"}`}
      onPress={onShowSources}
      style={{ alignSelf: "flex-start", marginTop: 10, whiteSpace: "nowrap" }}
    >
      <LogoStack>
        {sources.slice(0, 3).map((source) => (
          <SourceLogo key={source.id} domain={sourceDomain(source)} />
        ))}
      </LogoStack>
      <Text variant="text-small" color="var(--text-secondary)">
        {sources.length} {sources.length === 1 ? "source" : "sources"}
      </Text>
    </CollapsibleHeader>
  );
}

const SourcesScrollArea = styled(ChatpadListScrollArea)`
  position: relative;
`;

const SourceSection = styled("section")`
  &:not(:first-child) {
    border-top: 0.5px solid var(--border-alpha);
  }
  &:focus {
    outline: none;
  }
`;

const SourceSectionHeader = styled(ChatpadListHeader)`
  position: sticky;
  top: 0;
  z-index: 1;
  padding-inline: 16px;
  gap: 8px;
  background: var(--surface-secondary);

  > * {
    opacity: var(--source-header-opacity, 1);
  }
`;

const headerVisibilityThresholds = Array.from({ length: 41 }, (_, i) => i / 40);

type SourceScrollTarget = { messageId?: string };

function SourceGroup({
  group,
  scrollTarget,
  scrollRef,
}: {
  group: ResearchSourceGroup;
  scrollTarget?: SourceScrollTarget;
  scrollRef: RefObject<HTMLDivElement | null>;
}) {
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const headingId = useId();
  useEffect(() => {
    const header = headerRef.current;
    const scroller = scrollRef.current;
    if (!header || !scroller) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const leaving =
          entry.rootBounds &&
          entry.boundingClientRect.top < entry.rootBounds.top;
        // Finish fading in the first 12px of exit, before the title is clipped.
        const opacity = leaving
          ? Math.max(0, Math.min(1, (entry.intersectionRatio - 0.7) / 0.3))
          : 1;
        header.style.setProperty("--source-header-opacity", String(opacity));
      },
      { root: scroller, threshold: headerVisibilityThresholds }
    );
    observer.observe(header);
    return () => observer.disconnect();
  }, [scrollRef]);
  useEffect(() => {
    if (scrollTarget?.messageId !== group.messageId) return;
    const section = sectionRef.current;
    const scroller = scrollRef.current;
    if (!section || !scroller) return;
    section.focus({ preventScroll: true });
    const alignSection = () => {
      scroller.scrollTop = section.offsetTop;
    };
    alignSection();
    // Opening Chatpad animates its width, changing the preceding cards' height.
    // Keep the requested section aligned until the user starts navigating it.
    const observer = new ResizeObserver(alignSection);
    observer.observe(scroller);
    observer.observe(section);
    const stopAligning = () => observer.disconnect();
    const interactions = ["wheel", "touchstart", "pointerdown", "keydown"];
    for (const event of interactions)
      scroller.addEventListener(event, stopAligning, { passive: true });
    return () => {
      observer.disconnect();
      for (const event of interactions)
        scroller.removeEventListener(event, stopAligning);
    };
  }, [scrollTarget, group.messageId, scrollRef]);
  return (
    <SourceSection ref={sectionRef} tabIndex={-1} aria-labelledby={headingId}>
      <SourceSectionHeader ref={headerRef}>
        <h3 id={headingId} style={{ margin: 0, minWidth: 0 }}>
          <Text variant="text-small-md" ellipsis>
            {group.title}
          </Text>
        </h3>
        <CountBadge>
          <Text variant="text-smaller-md">{group.sources.length}</Text>
        </CountBadge>
      </SourceSectionHeader>
      <SourceList>
        {group.sources.map((source) => (
          <SourceCard key={source.url} source={source} />
        ))}
      </SourceList>
    </SourceSection>
  );
}

export function SourcesPanel({
  groups,
  scrollTarget,
  onClose,
  embedded = false,
}: {
  groups: readonly ResearchSourceGroup[];
  scrollTarget?: SourceScrollTarget;
  onClose: () => void;
  embedded?: boolean;
}) {
  const panelRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sourceCount = groups.reduce(
    (count, group) => count + group.sources.length,
    0
  );
  useEffect(() => {
    if (!scrollTarget?.messageId)
      panelRef.current?.focus({ preventScroll: true });
  }, [scrollTarget]);
  return (
    <section
      ref={panelRef}
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          onClose();
        }
      }}
      aria-label={`Sources · ${sourceCount}`}
      style={{ display: "flex", flex: 1, minWidth: 0, minHeight: 0 }}
    >
      <StepDetails
        embedded={embedded}
        flushBody
        heading={null}
        body={
          sourceCount ? (
            <ChatpadBody data-fill="true">
              <SourcesScrollArea ref={scrollRef}>
                {groups.map((group) => (
                  <SourceGroup
                    key={group.messageId}
                    group={group}
                    scrollTarget={scrollTarget}
                    scrollRef={scrollRef}
                  />
                ))}
              </SourcesScrollArea>
            </ChatpadBody>
          ) : (
            <EmptyState
              iconName="earth"
              title="No sources yet"
              description="Sources cited in this chat will appear here."
            />
          )
        }
      />
    </section>
  );
}
